import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"

import "./App.css"

import {
  boardDefault,
  boardStatusDefault,
  computeGuessStatus,
  LetterStatus,
} from "./helpers"
import { generateAfrikaansWordSet, wordBankManager } from "./wordBank"
import { getTodaysWord, startDailyWordMonitoring, stopDailyWordMonitoring, dailyWordSelector } from "./dailyWordSelector"
import { GameStateManager } from "./gameStateManager"
import { StatisticsManager } from "./statisticsManager"
import Board from "./components/Board"
import Keyboard from "./components/Keyboard"
import GameOver from "./components/GameOver"
import Statistics from "./components/Statistics"
import Fireworks from "./components/Fireworks"
import CongratulationsToast from "./components/CongratulationsToast"
import { getRandomCongratulationMessage } from "./congratulationsMessages"
import strings from "./strings"
import { useUser } from "./hooks/useUser"
import PWAInstallPrompt from "./components/PWAInstallPrompt"
import OfflineIndicator from "./components/OfflineIndicator"
import { useNetworkStatus } from "./utils/offlineUtils"

export interface IWordleGameContext {
  board: string[][]
  setBoard: Dispatch<SetStateAction<string[][]>>
  boardStatus: LetterStatus[][]
  setBoardStatus: Dispatch<SetStateAction<LetterStatus[][]>>
  currAttempt: { attempt: number; letterPos: number }
  setCurrAttempt: Dispatch<
    SetStateAction<{ attempt: number; letterPos: number }>
  >
  onDelete: () => void
  onEnter: () => void
  onSelectLetter: (key: string) => void
  correctWord: string
  letterStatus: Map<string, LetterStatus>
  setLetterStatus: Dispatch<SetStateAction<Map<string, LetterStatus>>>
  gameOver: { gameOver: boolean; guessedWord: boolean }
  setGameOver: Dispatch<
    SetStateAction<{ gameOver: boolean; guessedWord: boolean }>
  >
  revealingRow: number | null
  revealingBoardStatus: LetterStatus[][] | null
  vibratingRow: number | null
}

export const AppContext = createContext<IWordleGameContext>(
  {} as IWordleGameContext
)

function App() {
  const [board, setBoard] = useState(boardDefault)
  const [boardStatus, setBoardStatus] = useState(boardStatusDefault)
  const [currAttempt, setCurrAttempt] = useState({
    attempt: 0,
    letterPos: 0,
  })
  const [, setWordSet] = useState(new Set())
  const [letterStatus, setLetterStatus] = useState(new Map())
  const [gameOver, setGameOver] = useState({
    gameOver: false,
    guessedWord: false,
  })

  const [correctWord, setCorrectWord] = useState(strings.defaultWord)
  const [, setDateMonitoringId] = useState<number | null>(null)
  const [newWordNotification, setNewWordNotification] = useState<{
    show: boolean
    message: string
    newWord: string
  }>({ show: false, message: "", newWord: "" })
  
  const [wordBankNotification, setWordBankNotification] = useState<{
    show: boolean
    message: string
    isError: boolean
    canRetry: boolean
  }>({ show: false, message: "", isError: false, canRetry: false })
  
  const [revealingRow, setRevealingRow] = useState<number | null>(null)
  const [revealingBoardStatus, setRevealingBoardStatus] = useState<LetterStatus[][] | null>(null)
  const [invalidWordToast, setInvalidWordToast] = useState<{
    show: boolean
    message: string
  }>({ show: false, message: "" })
  const [vibratingRow, setVibratingRow] = useState<number | null>(null)
  
  // Celebration state
  const [showFireworks, setShowFireworks] = useState(false)
  const [congratulationsMessage, setCongratulationsMessage] = useState("")
  const [showCongratulations, setShowCongratulations] = useState(false)
  
  // User management
  const { user, loading: userLoading, submitGameResult } = useUser()
  
  // Refs to store timeout IDs for cleanup
  const vibrationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  



  // Initialize Afrikaans word system and daily word selection
  useEffect(() => {
    let monitoringId: number | null = null
    
    const initializeGame = async () => {
      try {
        // Set up error notification callback for word bank
        wordBankManager.setErrorNotificationCallback((message: string, isError: boolean) => {
          // Only show error notifications, never success notifications
          if (!isError) {
            return
          }
          
          setWordBankNotification({
            show: true,
            message,
            isError,
            canRetry: isError && wordBankManager.isUsingFallback()
          })
        })
        
        // Set up error notification callback for daily word selector
        dailyWordSelector.setErrorNotificationCallback((message: string, isError: boolean) => {
          // Only show error notifications, never success notifications
          if (!isError) {
            return
          }
          
          setWordBankNotification({
            show: true,
            message,
            isError,
            canRetry: false // Date calculation errors don't have manual retry
          })
        })
        
        // Set up error notification callback for game state manager
        GameStateManager.setErrorNotificationCallback((message: string, isError: boolean) => {
          // Only show error notifications, never success notifications
          if (!isError) {
            return
          }
          
          setWordBankNotification({
            show: true,
            message,
            isError,
            canRetry: false // Storage errors don't have manual retry
          })
        })
        
        // Load the Afrikaans word bank for validation
        const words = await generateAfrikaansWordSet()
        setWordSet(words.wordSet)
        
        // Get today's daily word
        const todaysWord = await getTodaysWord()
        setCorrectWord(todaysWord)
        
        // Load existing game state if available
        const existingGameState = GameStateManager.getCurrentGameState(todaysWord)
        if (existingGameState && existingGameState.currentWord === todaysWord) {
          // Restore game state from localStorage
          setBoard(existingGameState.board)
          setBoardStatus(existingGameState.boardStatus)
          setCurrAttempt({ 
            attempt: existingGameState.currentAttempt, 
            letterPos: existingGameState.letterPos 
          })
          setLetterStatus(existingGameState.letterStatus)
          setGameOver({ 
            gameOver: existingGameState.gameOver, 
            guessedWord: existingGameState.won 
          })
          
          // If the game was already won, replay the celebration
          if (existingGameState.gameOver && existingGameState.won) {
            const congratsMessage = getRandomCongratulationMessage(existingGameState.attempts)
            setCongratulationsMessage(congratsMessage)
            
            // Start celebration after a short delay to let the UI settle
            setTimeout(() => {
              // Only start if not already showing (prevent double celebrations)
              if (!showFireworks && !showCongratulations) {
                setShowFireworks(true)
                
                // Show congratulations toast after fireworks start
                setTimeout(() => {
                  setShowCongratulations(true)
                }, 1000)
              }
            }, 500)
          }
        }
        
        // Start monitoring for date changes to reset game
        monitoringId = startDailyWordMonitoring(() => {
          handleDateChange()
        })
        setDateMonitoringId(monitoringId)
        

        
      } catch (error) {
        console.error(strings.errors.initializingGame, error)
        // Fallback to default word if initialization fails
        setCorrectWord(strings.defaultWord)

      }
    }
    
    initializeGame()
    
    // Cleanup function to stop monitoring when component unmounts
    return () => {
      if (monitoringId !== null) {
        stopDailyWordMonitoring(monitoringId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Add visibility change listener to check for date changes when user returns
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // User returned to the tab, check if date has changed
        try {
          const currentWord = await getTodaysWord()
          if (currentWord !== correctWord) {
            // Date has changed while user was away
            await handleDateChange()
          }
        } catch (error) {
          console.error('Error checking for date change on visibility change:', error)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [correctWord])

  // Add focus listener as additional check for date changes
  useEffect(() => {
    const handleWindowFocus = async () => {
      try {
        const currentWord = await getTodaysWord()
        if (currentWord !== correctWord) {
          // Date has changed while window was not focused
          await handleDateChange()
        }
      } catch (error) {
        console.error('Error checking for date change on window focus:', error)
      }
    }

    window.addEventListener('focus', handleWindowFocus)
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [correctWord])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (vibrationTimeoutRef.current) {
        clearTimeout(vibrationTimeoutRef.current)
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
    }
  }, [])

  // Handle date change - reset game for new daily word
  const handleDateChange = async () => {
    try {
      const newWord = await getTodaysWord()
      
      // Show notification about new daily word
      setNewWordNotification({
        show: true,
        message: strings.newDailyWordAvailable,
        newWord: newWord
      })
      
      // Reset game state for new day using GameStateManager
      const newGameState = GameStateManager.resetForNewDay(newWord)
      
      // Update React state to match new game state
      setCorrectWord(newWord)
      setBoard(newGameState.board)
      setBoardStatus(newGameState.boardStatus)
      setCurrAttempt({ attempt: newGameState.currentAttempt, letterPos: newGameState.letterPos })
      setLetterStatus(newGameState.letterStatus)
      setGameOver({ gameOver: newGameState.gameOver, guessedWord: newGameState.won })
      
      // Reset celebration state
      setShowFireworks(false)
      setShowCongratulations(false)
      setCongratulationsMessage("")
      
      // Game reset for new daily word
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setNewWordNotification(prev => ({ ...prev, show: false }))
      }, 5000)
      
    } catch (error) {
      console.error('Error handling date change:', error)
      
      // Show error notification
      setNewWordNotification({
        show: true,
        message: strings.errorLoadingNewWord,
        newWord: ""
      })
      
      setTimeout(() => {
        setNewWordNotification(prev => ({ ...prev, show: false }))
      }, 5000)
    }
  }

  // Dismiss notification manually
  const dismissNotification = () => {
    setNewWordNotification(prev => ({ ...prev, show: false }))
  }

  // Dismiss word bank notification
  const dismissWordBankNotification = () => {
    setWordBankNotification(prev => ({ ...prev, show: false }))
  }

  // Handle fireworks completion
  const handleFireworksComplete = () => {
    setShowFireworks(false)
  }

  // Handle congratulations toast completion
  const handleCongratulationsComplete = () => {
    setShowCongratulations(false)
  }

  // Retry loading word bank
  const retryWordBankLoad = async () => {
    setWordBankNotification(prev => ({ ...prev, show: false }))
    
    try {
      const words = await wordBankManager.retryLoadWordBank()
      setWordSet(words)
      
      // If successful, get new daily word with updated word bank
      const todaysWord = await getTodaysWord()
      setCorrectWord(todaysWord)
      
      // Reset game state if word changed
      const newGameState = GameStateManager.resetForNewDay(todaysWord)
      setBoard(newGameState.board)
      setBoardStatus(newGameState.boardStatus)
      setCurrAttempt({ attempt: newGameState.currentAttempt, letterPos: newGameState.letterPos })
      setLetterStatus(newGameState.letterStatus)
      setGameOver({ gameOver: newGameState.gameOver, guessedWord: newGameState.won })
      
      // Reset celebration state
      setShowFireworks(false)
      setShowCongratulations(false)
      setCongratulationsMessage("")
      
      // Success - no notification needed
      
    } catch (error) {
      console.error('Manual retry failed:', error)
      setWordBankNotification({
        show: true,
        message: strings.wordBankRetryFailed,
        isError: true,
        canRetry: true
      })
    }
  }

  const onSelectLetter = (key: string) => {
    // Prevent input if game is over
    if (gameOver.gameOver) return
    
    // Prevent input during reveal animations (but allow during vibration)
    if (revealingRow !== null) return
    
    // Prevent input if current row is full
    if (currAttempt.letterPos >= 5) return
    
    // Prevent input if we've exceeded maximum attempts
    if (currAttempt.attempt >= 6) return
    
    const newBoard = [...board]
    newBoard[currAttempt.attempt][currAttempt.letterPos] = key
    setBoard(newBoard)
    setCurrAttempt({ ...currAttempt, letterPos: currAttempt.letterPos + 1 })
  }

  const onDelete = () => {
    // Prevent deletion if game is over
    if (gameOver.gameOver) return
    
    // Prevent deletion during reveal animations (but allow during vibration)
    if (revealingRow !== null) return
    
    // Prevent deletion if no letters to delete
    if (currAttempt.letterPos === 0) return
    
    // Prevent deletion if we've exceeded maximum attempts
    if (currAttempt.attempt >= 6) return
    
    const newBoard = [...board]
    newBoard[currAttempt.attempt][currAttempt.letterPos - 1] = ""
    setBoard(newBoard)
    setCurrAttempt({ ...currAttempt, letterPos: currAttempt.letterPos - 1 })
  }

  const onEnter = () => {
    // Prevent any action if game is over
    if (gameOver.gameOver) return
    
    // Ensure we have a complete word
    if (currAttempt.letterPos !== 5) return

    // Prevent multiple rapid submissions during reveal animations
    if (revealingRow !== null) return
    
    // Prevent rapid resubmission during vibration
    if (vibratingRow !== null) return

    let currWord = board[currAttempt.attempt].join("").toUpperCase()
    if (!wordBankManager.isValidWord(currWord)) {
      // Clear any existing timeouts to prevent conflicts
      if (vibrationTimeoutRef.current) {
        clearTimeout(vibrationTimeoutRef.current)
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
      
      // Show vibration animation on current row
      setVibratingRow(currAttempt.attempt)
      
      // Show toast message
      setInvalidWordToast({
        show: true,
        message: strings.wordNotFound
      })
      
      // Set up cleanup timeouts
      vibrationTimeoutRef.current = setTimeout(() => {
        setVibratingRow(null)
        vibrationTimeoutRef.current = null
      }, 500)
      
      toastTimeoutRef.current = setTimeout(() => {
        setInvalidWordToast({ show: false, message: "" })
        toastTimeoutRef.current = null
      }, 2000)
      
      // IMPORTANT: Don't advance to next row for invalid words
      // User should be able to continue editing the current row
      return
    }

    // compute the status of the letters
    const newBoardStatus = [...boardStatus]
    newBoardStatus[currAttempt.attempt] = computeGuessStatus(
      currWord,
      correctWord
    )

    // Start the reveal animation
    setRevealingRow(currAttempt.attempt)
    setRevealingBoardStatus(newBoardStatus)

    // After all letters are revealed, update game state
    setTimeout(() => {
      setBoardStatus(newBoardStatus)
      setRevealingRow(null)
      setRevealingBoardStatus(null)
      
      // defining here because it won't be refreshed after the setCurrAttempt
      const nextAttemptCount = currAttempt.attempt + 1
      const isGameWon = currWord === correctWord
      const isGameOver = isGameWon || nextAttemptCount === 6

      setCurrAttempt({
        attempt: nextAttemptCount,
        letterPos: 0,
      })

      if (isGameWon) {
        setGameOver({
          gameOver: true,
          guessedWord: true,
        })
        
        // Trigger celebration animations and messages
        const congratsMessage = getRandomCongratulationMessage(nextAttemptCount)
        setCongratulationsMessage(congratsMessage)
        
        // Start fireworks immediately
        setShowFireworks(true)
        
        // Show congratulations toast after a short delay
        setTimeout(() => {
          setShowCongratulations(true)
        }, 1000)
        
      } else if (nextAttemptCount === 6) {
        setGameOver({
          gameOver: true,
          guessedWord: false,
        })
        
        // Show failure message
        const failureMessage = getRandomCongratulationMessage(0) // 0 for failed
        setCongratulationsMessage(failureMessage)
        
        // Show failure toast after a short delay
        setTimeout(() => {
          setShowCongratulations(true)
        }, 1000)
      }

      // Save game state to localStorage and record statistics
      try {
        const currentDate = new Date().toLocaleDateString('en-CA')
        const guesses = [...board.slice(0, currAttempt.attempt), board[currAttempt.attempt]]
          .slice(0, nextAttemptCount)
          .map(row => row.join(""))
          .filter(guess => guess.length === 5)

        const gameState = {
          date: currentDate,
          completed: isGameOver,
          won: isGameWon,
          attempts: nextAttemptCount,
          guesses: guesses,
          currentWord: correctWord,
          board: [...board],
          boardStatus: newBoardStatus,
          currentAttempt: nextAttemptCount,
          letterPos: 0,
          letterStatus: new Map(letterStatus),
          gameOver: isGameOver
        }

        GameStateManager.saveGameState(gameState)

        // Record statistics if game is completed
        if (isGameOver) {
          StatisticsManager.recordGameCompletion(isGameWon, nextAttemptCount, guesses)
          
          // Submit game result to database
          if (user && submitGameResult) {
            submitGameResult(correctWord, nextAttemptCount, guesses)
              .then(() => {
                console.log('Game result submitted to database successfully')
              })
              .catch((error) => {
                console.error('Failed to submit game result to database:', error)
              })
          }
        }
      } catch (error) {
        console.error('Error saving game state:', error)
      }
    }, 5 * 300 + 600) // Wait for all letters (300ms each) + extra time for the last animation (600ms flip + 100ms pause)
  }

  const [showStatistics, setShowStatistics] = useState(false)

  const handleShowStatistics = () => {
    setShowStatistics(true)
  }

  const handleCloseStatistics = () => {
    setShowStatistics(false)
  }

  const { isOffline } = useNetworkStatus();

  return (
    <div className={`App ${isOffline ? 'offline' : ''}`}>
      <OfflineIndicator />
      <nav>
        <div className="nav-content">
          <h1>{strings.appTitle}</h1>
          <button 
            className="nav-stats-button"
            onClick={handleShowStatistics}
            aria-label={strings.a11y.viewStatistics}
          >
            {strings.viewStatistics}
          </button>
        </div>
      </nav>
      
      {/* New Daily Word Notification */}
      {newWordNotification.show && (
        <div className="notification-banner">
          <div className="notification-content">
            <span className="notification-message">{newWordNotification.message}</span>
            <button 
              className="notification-dismiss" 
              onClick={dismissNotification}
              aria-label={strings.a11y.dismissNotification}
            >
              {strings.dismissNotification}
            </button>
          </div>
        </div>
      )}
      
      {/* Word Bank Error Notification */}
      {wordBankNotification.show && (
        <div className={`notification-banner ${wordBankNotification.isError ? 'notification-error' : 'notification-success'}`}>
          <div className="notification-content">
            <span className="notification-message">{wordBankNotification.message}</span>
            <div className="notification-actions">
              {wordBankNotification.canRetry && (
                <button 
                  className="notification-retry" 
                  onClick={retryWordBankLoad}
                  aria-label={strings.a11y.retryLoadingWordBank}
                >
                  {strings.retry}
                </button>
              )}
              <button 
                className="notification-dismiss" 
                onClick={dismissWordBankNotification}
                aria-label={strings.a11y.dismissNotification}
              >
                {strings.dismissNotification}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Invalid Word Toast */}
      {invalidWordToast.show && (
        <div className="invalid-word-toast">
          {invalidWordToast.message}
        </div>
      )}
      
      <AppContext.Provider
        value={{
          board,
          setBoard,
          boardStatus,
          setBoardStatus,
          currAttempt,
          setCurrAttempt,
          onDelete,
          onEnter,
          onSelectLetter,
          correctWord,
          letterStatus,
          setLetterStatus,
          gameOver,
          setGameOver,
          revealingRow,
          revealingBoardStatus,
          vibratingRow,
        }}
      >
        <div className="game">
          <Board />
          {gameOver.gameOver ? <GameOver /> : <Keyboard />}
        </div>
      </AppContext.Provider>
      
      <Statistics 
        isVisible={showStatistics}
        onClose={handleCloseStatistics}
      />
      
      {/* Fireworks Animation */}
      <Fireworks 
        isActive={showFireworks}
        onComplete={handleFireworksComplete}
      />
      
      {/* Congratulations Toast */}
      <CongratulationsToast 
        message={congratulationsMessage}
        isVisible={showCongratulations}
        onComplete={handleCongratulationsComplete}
      />
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  )
}

export default App
